import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMFacultyForm = ({ initialData, campuses, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("universityManager");
  const { t: tErrors } = useTranslation("errors");
  const { can } = useUniAdminAuth();
  const canListCampuses = can(PERMISSIONS.campus.list);

  const [formData, setFormData] = useState({
    campusId: "",
    nameEn: "",
    nameLocal: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        campusId: initialData.campusId || "",
        nameEn: initialData.nameEn || "",
        nameLocal: initialData.nameLocal || "",
      });
    } else {
      setFormData({ campusId: "", nameEn: "", nameLocal: "" });
    }
    setErrors({});
  }, [initialData]);

  const validate = () => {
    const newErrors = {};
    if (!formData.campusId) newErrors.campusId = t("crud.errors.campusRequired");
    if (!formData.nameEn.trim()) newErrors.nameEn = t("crud.errors.nameEnRequired");
    if (!formData.nameLocal.trim()) newErrors.nameLocal = t("crud.errors.nameLocalRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate() || loading) return;
    onSubmit({
      campusId: formData.campusId,
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Campus Selection */}
      <div className="mb-3">
        <label className="form-label fw-medium">
          {t("crud.fields.campus")} <span className="text-danger">*</span>
        </label>
        <select
          className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
          value={formData.campusId}
          onChange={(e) => handleChange("campusId", e.target.value)}
          disabled={loading || !canListCampuses}
          required
        >
          <option value="">{t("crud.selectCampus")}</option>
          {campuses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nameEn}
            </option>
          ))}
        </select>
        {errors.campusId && <div className="invalid-feedback">{errors.campusId}</div>}
        {!canListCampuses && <small className="text-danger">{tErrors("permission.denied")}</small>}
      </div>

      {/* Faculty Names */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameEn")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
            value={formData.nameEn}
            onChange={(e) => handleChange("nameEn", e.target.value)}
            placeholder={t("crud.placeholders.nameEn")}
            disabled={loading}
          />
          {errors.nameEn && <div className="invalid-feedback">{errors.nameEn}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameLocal")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
            value={formData.nameLocal}
            onChange={(e) => handleChange("nameLocal", e.target.value)}
            placeholder={t("crud.placeholders.nameLocal")}
            disabled={loading}
          />
          {errors.nameLocal && <div className="invalid-feedback">{errors.nameLocal}</div>}
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        {onCancel && (
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={loading}>
            <i className="fas fa-times me-2"></i>
            {t("crud.cancel")}
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              {t("crud.saving")}
            </>
          ) : (
            <>
              <i className={`fas ${initialData ? "fa-save" : "fa-plus"} me-2`}></i>
              {initialData ? t("crud.save") : t("crud.addFaculty")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UMFacultyForm;
